import { Plugin } from "prosemirror-state";
import type { Fragment, Node as PmNode, Slice } from "prosemirror-model";
import { cardTitle } from "../card-title";
import { cardContent } from "./cards";

const INDENT = "  ";

function linkedTitle(node: PmNode): string {
  const content = cardContent(node.attrs["uuid"] as string);
  return content === undefined ? "…" : cardTitle(content);
}

function inlineText(node: PmNode): string {
  let out = "";
  node.forEach((child) => {
    if (child.isText) out += child.text ?? "";
    else if (child.type.name === "card_link") out += linkedTitle(child);
  });
  return out;
}

function fragmentText(frag: Fragment, indent: string, separator: string): string {
  const parts: string[] = [];
  frag.forEach((child) => {
    parts.push(blockText(child, indent));
  });
  return parts.join(separator);
}

function listText(list: PmNode, indent: string): string {
  const style = list.attrs["style"] as string;
  let counter = (list.attrs["order"] as number) || 1;
  const lines: string[] = [];

  list.forEach((item) => {
    const marker =
      style === "ordered"
        ? `${counter++}. `
        : style === "todo"
        ? item.attrs["checked"]
          ? "[x] "
          : "[ ] "
        : "- ";
    // A copied slice can start after the paragraph of an item. Then the item
    // has no marker line. The blocks after the paragraph indent one more level.
    item.forEach((child, _offset, index) => {
      if (index === 0 && child.isTextblock) lines.push(indent + marker + inlineText(child));
      else lines.push(blockText(child, indent + INDENT));
    });
  });
  return lines.join("\n");
}

function blockText(node: PmNode, indent: string): string {
  switch (node.type.name) {
    case "heading":
      return `${indent}${"#".repeat(node.attrs["level"] as number)} ${inlineText(node)}`;
    case "code_block": {
      const body = node.textContent.split("\n").map((line) => indent + line);
      return [`${indent}\`\`\``, ...body, `${indent}\`\`\``].join("\n");
    }
    case "list":
      return listText(node, indent);
    // Atoms have no text content, so they need an explicit case.
    case "card_embed":
      return indent + linkedTitle(node);
    case "view_block":
      return `${indent}[${node.attrs["viewType"] as string} view]`;
    default:
      return node.isTextblock
        ? indent + inlineText(node)
        : fragmentText(node.content, indent, "\n");
  }
}

// Returns the textblock that contains both ends of the slice, if one exists.
function enclosingTextblock(slice: Slice): PmNode | undefined {
  const openDepth = Math.min(slice.openStart, slice.openEnd);
  let frag = slice.content;
  for (let depth = 0; depth < openDepth && frag.childCount === 1; depth++) {
    const child = frag.child(0);
    if (child.isTextblock) return child;
    frag = child.content;
  }
  return undefined;
}

/** Writes the plain-text clipboard format. This function keeps the block and
 * list structure as markdown, which the ProseMirror default removes. Text from
 * one textblock gets no markdown, because it is part of a line. */
export function clipboardTextSerializer(slice: Slice): string {
  const textblock = enclosingTextblock(slice);
  return textblock ? inlineText(textblock) : fragmentText(slice.content, "", "\n\n");
}

export function clipboardTextPlugin(): Plugin {
  return new Plugin({ props: { clipboardTextSerializer } });
}
